import { useQuery } from '@apollo/client';
import { buttonV2Class } from '@synoptic/ui-kit/button-v2/button-v2.css.js';
import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { ArrowRightIcon } from '@synoptic/ui-kit/icons/react/arrow-right.js';
import { NavArrowRightIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-right.js';
import { SkeletonV2 } from '@synoptic/ui-kit/skeleton-v2/skeleton-v2.js';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
} from 'motion/react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ExploreTerminalPostsQueryQuery } from '@/__generated__/graphql.ts';
import { gql } from '@/__generated__/index.ts';
import { TextEditor } from '@/components/text-editor/text-editor';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { fetchPolymarketMarketById } from '@/routes/_app.terminal._index/terminal/services/polymarket-markets-api.ts';
import {
  exploreSectionHeaderV2,
  exploreSectionTitleV2,
} from './explore-page-v2.css.ts';
import {
  EXPLORE_TERMINAL_POST_LIMIT,
  EXPLORE_TERMINAL_STREAM_ID,
  formatPostClock,
  formatVolume,
  marketIdsFromEnrichments,
  marketIdsToFetch,
  padIndex,
  slideIndexFor,
  slidesForPosts,
  splitPrefixedPost,
  stepSlide,
  terminalRoll,
  yesNoCents,
  type TerminalPost,
  type TerminalSlide,
} from './explore-terminal-model.ts';
import {
  terminalArrowPrevV2,
  terminalArrowsV2,
  terminalClockTotalV2,
  terminalClockV2,
  terminalDeckV2,
  terminalFeedV2,
  terminalPostsTrackV2,
  terminalLiveCoreV2,
  terminalLiveMarkV2,
  terminalLiveV2,
  terminalMarketBodyV2,
  terminalMarketCopyV2,
  terminalMarketHeaderV2,
  terminalMarketToolsV2,
  terminalMarketV2,
  terminalMetaV2,
  terminalNoV2,
  terminalOutcomePriceV2,
  terminalOutcomesV2,
  terminalPanelV2,
  terminalPostCopyV2,
  terminalPostHeadlineEditableV2,
  terminalPostHeadlineV2,
  terminalPostLineV2,
  terminalPostSkeletonV2,
  terminalPostSourceV2,
  terminalPostTimeV2,
  terminalPostV2,
  terminalQuestionV2,
  terminalSectionV2,
  terminalYesV2,
} from './explore-terminal-section-v2.css.ts';

const POST_POLL_MS = 30_000;
const ROLL_DURATION_S = 0.45;

const ExploreTerminalPostsQuery = gql(`
  query ExploreTerminalPostsQuery(
    $id: ID!
    $matchFilter: PostMatchFilterInput
    $limit: Int
  ) {
    publicStreamPostPreview(
      id: $id
      matchFilter: $matchFilter
      limit: $limit
    ) {
      items {
        id
        text
        createdAt
        enrichments {
          polymatcher
        }
      }
    }
  }
`);

type LoadedMarket = Awaited<ReturnType<typeof fetchPolymarketMarketById>>;

type Roll = {
  track: TerminalPost[];
  offset: number;
  height: number;
  next: TerminalPost[];
};

const ARROWS = [
  { delta: -1, label: 'Previous market', iconClassName: terminalArrowPrevV2 },
  { delta: 1, label: 'Next market', iconClassName: undefined },
];

const OUTCOMES = [
  { key: 'yes', label: 'Yes', className: terminalYesV2 },
  { key: 'no', label: 'No', className: terminalNoV2 },
] as const;

const postsFromQuery = (
  items: ExploreTerminalPostsQueryQuery['publicStreamPostPreview']['items'],
): TerminalPost[] =>
  items.flatMap((item) => {
    const marketIds = marketIdsFromEnrichments(item.enrichments?.polymatcher);
    if (!item.text || !marketIds.length) return [];
    const { source, headline } = splitPrefixedPost(item.text);
    return [
      {
        id: item.id,
        time: formatPostClock(item.createdAt),
        headline,
        source,
        marketIds,
      },
    ];
  });

export const ExploreTerminalSectionV2 = () => {
  const titleId = useId();
  const postsQuery = useQuery(ExploreTerminalPostsQuery, {
    variables: {
      id: EXPLORE_TERMINAL_STREAM_ID,
      matchFilter: { polymarket: true },
      limit: EXPLORE_TERMINAL_POST_LIMIT,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: POST_POLL_MS,
  });

  const incomingPosts = useMemo(
    () => postsFromQuery(postsQuery.data?.publicStreamPostPreview.items ?? []),
    [postsQuery.data],
  );
  const [posts, setPosts] = useState(incomingPosts);
  const [roll, setRoll] = useState<Roll | null>(null);
  const reducedMotion = useReducedMotion();
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const columnY = useMotionValue(0);

  useEffect(() => {
    if (roll) return;
    const plan = reducedMotion ? null : terminalRoll(posts, incomingPosts);
    const rows = trackRef.current?.children;
    const pitch =
      rows && rows.length >= 2
        ? (rows[1] as HTMLElement).offsetTop -
          (rows[0] as HTMLElement).offsetTop
        : 0;
    const height = viewportRef.current?.getBoundingClientRect().height ?? 0;
    if (!plan || pitch <= 0 || height <= 0) {
      setPosts(incomingPosts);
      return;
    }
    setRoll({
      track: plan.track,
      offset: plan.shift * pitch,
      height,
      next: incomingPosts,
    });
  }, [incomingPosts, posts, roll, reducedMotion]);

  useLayoutEffect(() => {
    if (!roll) return;
    columnY.set(-roll.offset);
    const controls = animate(columnY, 0, {
      duration: ROLL_DURATION_S,
      ease: [0.22, 1, 0.36, 1],
      onComplete: () => {
        setPosts(roll.next);
        setRoll(null);
      },
    });
    return () => controls.stop();
  }, [columnY, roll]);

  const showSkeleton =
    posts.length === 0 && (postsQuery.loading || incomingPosts.length > 0);
  const slides = useMemo(() => slidesForPosts(posts), [posts]);
  const [active, setActive] = useState<TerminalSlide | null>(null);
  const slideIndex = slideIndexFor(slides, active);
  const slide = slides[slideIndex] ?? null;

  const marketKey = [...new Set(posts.flatMap((post) => post.marketIds))].join(
    '|',
  );
  const [markets, setMarkets] = useState<Record<string, LoadedMarket>>({});
  const marketsRef = useRef(markets);
  marketsRef.current = markets;

  useEffect(() => {
    const ids = marketIdsToFetch(marketKey, marketsRef.current);
    if (ids.length === 0) return;
    let cancelled = false;
    for (const id of ids) {
      void fetchPolymarketMarketById(id)
        .catch(() => null)
        .then((market) => {
          if (!cancelled)
            setMarkets((current) =>
              current[id] !== undefined
                ? current
                : { ...current, [id]: market },
            );
        });
    }
    return () => {
      cancelled = true;
    };
  }, [marketKey]);

  const market = slide ? markets[slide.marketId] : undefined;
  const marketPending = Boolean(slide) && market === undefined;
  const prices = market ? yesNoCents(market.bestBid, market.bestAsk) : null;
  const volume = market ? formatVolume(market.volume) : '';

  const activatePost = (postId: string) => {
    const match = slides.find((item) => item.postId === postId);
    if (match) setActive(match);
  };

  const moveSlide = (delta: number) => {
    const next = slides[stepSlide(slideIndex, delta, slides.length)];
    if (next) setActive(next);
  };

  return (
    <section
      className={terminalSectionV2}
      aria-labelledby={titleId}
      data-testid="explore-terminal-v2"
    >
      <header className={exploreSectionHeaderV2}>
        <h2 id={titleId} className={exploreSectionTitleV2}>
          Your Terminal
        </h2>
        <Link className={buttonV2Class.primary} to={paths.terminal}>
          Open terminal
          <ArrowRightIcon />
        </Link>
      </header>
      <div className={terminalPanelV2}>
        <div className={terminalFeedV2}>
          <div
            ref={viewportRef}
            role="listbox"
            aria-label="Matched posts"
            style={
              roll
                ? { height: roll.height, clipPath: 'inset(0 -32px 0 0)' }
                : undefined
            }
          >
            <motion.div
              ref={trackRef}
              className={terminalPostsTrackV2}
              style={{ y: columnY }}
            >
              {showSkeleton
                ? Array.from(
                    { length: EXPLORE_TERMINAL_POST_LIMIT },
                    (_, index) => (
                      <SkeletonV2
                        key={index}
                        className={terminalPostSkeletonV2}
                        height={64}
                        width="100%"
                      />
                    ),
                  )
                : (roll?.track ?? posts).map((post) => {
                    const selected = post.id === slide?.postId;
                    return (
                      <div
                        key={post.id}
                        role="option"
                        tabIndex={0}
                        aria-selected={selected}
                        data-active={selected ? 'true' : 'false'}
                        data-testid="explore-terminal-post"
                        className={terminalPostV2}
                        onMouseEnter={() => activatePost(post.id)}
                        onFocus={() => activatePost(post.id)}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ')
                            return;
                          event.preventDefault();
                          activatePost(post.id);
                        }}
                      >
                        <div className={terminalPostCopyV2}>
                          <div className={terminalPostLineV2}>
                            <span className={terminalPostTimeV2}>
                              {post.time}
                            </span>
                            <TextEditor
                              namespace={`explore-terminal-post:${post.id}`}
                              className={terminalPostHeadlineV2}
                              contentEditableClassName={
                                terminalPostHeadlineEditableV2
                              }
                              initialState={post.headline}
                              format="md"
                              editable={false}
                              disabled
                              rich={false}
                              theme="small"
                            />
                          </div>
                          {post.source ? (
                            <span className={terminalPostSourceV2}>
                              {post.source}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
            </motion.div>
          </div>
        </div>
        <div className={terminalDeckV2}>
          <article
            className={terminalMarketV2}
            data-testid="explore-terminal-market"
            data-market-id={slide?.marketId ?? ''}
          >
            <div className={terminalMarketHeaderV2}>
              <div className={terminalLiveV2}>
                <span className={terminalLiveMarkV2} aria-hidden>
                  <span className={terminalLiveCoreV2} />
                </span>
                LIVE
              </div>
              <div className={terminalMarketToolsV2}>
                <div className={terminalClockV2}>
                  <span>{padIndex(slides.length ? slideIndex + 1 : 0)}</span>
                  <span className={terminalClockTotalV2}>
                    /{padIndex(slides.length)}
                  </span>
                </div>
                <div className={terminalArrowsV2}>
                  {ARROWS.map(({ delta, label, iconClassName }) => (
                    <ButtonV2
                      key={delta}
                      type="button"
                      variant="secondary"
                      size="icon-sm"
                      aria-label={label}
                      disabled={slides.length < 2}
                      onClick={() => moveSlide(delta)}
                    >
                      <NavArrowRightIcon className={iconClassName} />
                    </ButtonV2>
                  ))}
                </div>
              </div>
            </div>
            {showSkeleton || marketPending ? (
              <SkeletonV2 height={120} width="100%" />
            ) : market ? (
              <div className={terminalMarketBodyV2}>
                <div className={terminalMarketCopyV2}>
                  <h3 className={terminalQuestionV2}>
                    {market.question || 'Matched market'}
                  </h3>
                  {volume ? (
                    <div className={terminalMetaV2}>{volume}</div>
                  ) : null}
                </div>
                <div className={terminalOutcomesV2}>
                  {OUTCOMES.map(({ key, label, className }) => (
                    <Link
                      key={key}
                      className={className}
                      to={paths.terminal}
                      data-testid={`explore-terminal-${key}`}
                    >
                      {label}
                      {prices ? (
                        <span className={terminalOutcomePriceV2}>
                          {prices[key]}
                        </span>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}
          </article>
        </div>
      </div>
    </section>
  );
};
